import CustomTable from "components/common/CustomTable";
import VendorAvatar from "components/common/VendorAvatar";
import { formatMoney } from "helpers";
import { displayProductChannelIcon } from "helpers/displayProductChannelIcon";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import { truncate } from "lodash";
import millify from "millify";
import moment from "moment";
import React, { useEffect, useState } from "react";
import ResponsivePaginationComponent from "react-responsive-pagination";
import { Tooltip } from "react-tooltip";
import { inventoryActions } from "redux/Ldb/actions";
import { getSalesChannelIconRaw } from "utils/get-sales-channel-icon-raw";

type ViewProductListProps = {
    resultMessage: {
        message: string;
        products: Record<string, any>;
        success: boolean;
        pagination: {
            current: number;
            number_of_pages: number;
            perPage: number;
        };
        total: number;
    };
    toolName: string;
};

const ViewProductList = ({ resultMessage, toolName }: ViewProductListProps) => {
    const dispatch = useAppDispatch();
    const [page, setPage] = useState<{
        currentPage: number;
        noOfPages: number;
    }>({
        currentPage: 1,
        noOfPages: 1,
    });
    const [productList, setProductList] = useState<Record<string, any>[]>([]);

    const { fetchingInventoryList, fetchedInventoryListSuccess } = useAppSelector(
        (state) => state.inventory
    );

    useEffect(() => {
        if (page?.currentPage !== 1) {
            dispatch(
                inventoryActions.fetchInventoryList(
                    resultMessage?.pagination?.perPage || 10,
                    page?.currentPage,
                    "" //  searchQuery || debouncedSearch,
                    //  productCategory,
                    //  channelQuery || channel,
                    //  tracking,
                    //  variantTableFilter?.sortBy,
                    //  variantTableFilter?.sortDirection,
                    //  stockLevelRange?.stockLevelMin,
                    //  stockLevelRange?.stockLevelMax,
                    //  forecastedNeedsRange?.forecastedDemandMin,
                    //  forecastedNeedsRange?.forecastedDemandMax,
                    //  inventoryDate?.startDate,
                    //  inventoryDate?.endDate
                )
            );
        }
        return () => {
            dispatch(inventoryActions.resetFetchInventoryList());
        };
    }, [
        dispatch,
        page?.currentPage,
        //  debouncedSearch,
        //  productCategory,
        //  !inventory ? channel : "",
        //  tracking,
        //  refetch,
        //  channelQuery,
        //  searchQuery,
        //  categoryQuery,
        //  applyProductFilter,
        //  variantTableFilter?.sortDirection,
        //  variantTableFilter?.sortBy,
        //  updatedPage,
        //  inventoryDate,
    ]);

    useEffect(() => {
        if (fetchedInventoryListSuccess) {
            setProductList(fetchedInventoryListSuccess?.inventory?.data);
        }
        dispatch(inventoryActions.resetFetchInventoryList());
    }, [fetchedInventoryListSuccess, dispatch]);

    const tableData = page?.currentPage === 1 ? resultMessage?.products : productList;
    const tableHeader = [
        {
            title: "Product name",
            widthClass: "w-[300px]",
        },
        {
            title: "Curr. stock level",
            widthClass: "w-[158px]",
            tooltipIcon: "current-stock-level",
            tooltipContent:
                "The actual quantity of the product currently available in stock for sale or use at this moment.",
        },
        {
            title: "Categories",
            widthClass: "w-[158px]",
            tooltipIcon: "categories",
            tooltipContent:
                "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },
        {
            title: "Channel",
            widthClass: "w-[158px]",
            // tooltipIcon: "channel",
            // tooltipContent:
            //     "Groups or classifications that organize products such as Finished goods, Components, and Work-in-Progress (WIP), helping you manage your inventory efficiently.",
        },

        {
            title: "Forecasted needs",
            widthClass: "w-[158px]",
            tooltipIcon: "forecasted-sales",
            tooltipContent:
                "The estimated amount of the product required to meet anticipated demand over your selected forecast window (between 1 and 6 months)",
        },
        {
            title: "Forecasted stockout date",
            widthClass: "w-[158px]",
            tooltipIcon: "forecasted-stockout-date",
            tooltipContent:
                "This is the projected date when this product is expected to run out of stock based on current inventory levels and demand trends. Use it to stay ahead with timely reorders.",
        },
    ];

    const tableBody = tableData?.map((product, idx) => {
        const givenDate = moment(product?.forcastData?.stockoutDate);
        const today = moment().startOf("day");
        let differenceInDays = 0;

        if (givenDate.isValid()) {
            differenceInDays = givenDate.diff(today, "days");
        }

        return [
            {
                content: (
                    <div
                        className={`py-4 px-3 flex font-rocGroteskMedium items-center h-full text-sm text-g-75 `}
                    >
                        <div className="flex items-center space-x-3">
                            {product?.productImageDetails?.productAvatar ||
                            product?.productImageDetails?.productImages?.[0] ? (
                                <VendorAvatar
                                    imageSrc={
                                        product?.productImageDetails?.productAvatar ||
                                        product?.productImageDetails?.productImages?.[0]
                                    }
                                    size={32}
                                    name={product?.productName}
                                    containerClassname="!rounded-md"
                                />
                            ) : (
                                <div className="w-8 h-8 rounded shrink-0 flex items-center justify-center bg-slate-200 ">
                                    <i className="ri-box-2-fill text-lg text-slate-500"></i>
                                </div>
                            )}
                            <div>
                                <div>
                                    <a id={`view-product-product-column-${idx}`}>
                                        <span className="block text-sm">
                                            {truncate(product?.productName, {
                                                length: 30,
                                            })}
                                        </span>
                                        <span className="text-slate-500 text-sm">
                                            {product?.sku ? `SKU-${product?.sku}` : ""}
                                        </span>
                                    </a>
                                    <Tooltip
                                        anchorSelect={`#product-column-${idx}`}
                                        place={"right"}
                                        content={product?.productName}
                                        style={{
                                            maxWidth: "250px",
                                            zIndex: 9999,
                                            background: "#fff",
                                            borderRadius: "6px",
                                            border: "1px solid #E2E8F0",
                                            color: "#1E293B",
                                            boxShadow: "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ),

                tableDataDivClass: "!pl-0",
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <span>
                            {product?.productStockDetails?.stockLevel?.toLocaleString() || "0"}
                            {` `}
                            {product?.productStockDetails?.unitOfMeasurement || ""}
                        </span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        <span>{product?.inventoryCategory || "Not Available"}</span>
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-g-75 `}>
                        {product?.channels?.length === 0 ? (
                            "Not available"
                        ) : (
                            <div>
                                <div className="flex items-centers gap-0">
                                    {product?.channels?.map((channel, index) => {
                                        const channelIcon = getSalesChannelIconRaw(channel);
                                        return (
                                            <div key={`channel-${idx}-${index}`}>
                                                <Tooltip
                                                    anchorSelect={`#channel-${idx}-${index}`}
                                                    place={"top"}
                                                    content={channel}
                                                    style={{
                                                        maxWidth: "250px",
                                                        zIndex: 9999,
                                                        background: "#142837",
                                                        borderRadius: "6px",
                                                        border: "1px solid #142837",
                                                        color: "#fff",
                                                        boxShadow:
                                                            "0px 4px 16px 0px rgba(0, 0, 0, 0.04)",
                                                    }}
                                                />
                                                <div
                                                    id={`channel-${idx}-${index}`}
                                                    className={
                                                        "rounded-full bg-white w-[26px] h-[26px] flex items-center justify-center border border-slate-200 p-[3px] shadow-lg cursor-pointer"
                                                    }
                                                    style={{
                                                        ...(product?.channels?.length !==
                                                            index + 1 && {
                                                            transform: `translateX(${
                                                                (index + 1) * 6
                                                            }px)`,
                                                        }),
                                                    }}
                                                >
                                                    {typeof channelIcon === "string" ? (
                                                        <img src={channelIcon} alt="" />
                                                    ) : (
                                                        <>{channelIcon}</>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                ),
                widthClass: "!border-slate-200",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <div className="flex items-center space-x-1.5">
                            {Boolean(product?.forcastData?.forecastedNeeds) && (
                                <div>
                                    <a id={`view-product-forecast-needs-${idx}`}>
                                        <span className="material-symbols-outlined text-[14px] text-r-50">
                                            warning
                                        </span>
                                    </a>

                                    <Tooltip
                                        anchorSelect={`#forecast-needs-${idx}`}
                                        place={"top"}
                                        content={
                                            Number(product?.productStockDetails?.stockLevel) < 1
                                                ? "Product is out of stock"
                                                : "Stock for this product may soon run out."
                                        }
                                        style={{ width: "180px", zIndex: 9999 }}
                                    />
                                </div>
                            )}
                            {product?.forcastData?.forecastedNeeds ? (
                                <span className="text-[#FF3D1D]">
                                    {millify(product?.forcastData?.forecastedNeeds, {
                                        precision: 2,
                                    })}{" "}
                                    {product?.productStockDetails?.unitOfMeasurement || ""}
                                </span>
                            ) : !product?.productStockDetails?.stockLevel ||
                              product?.productStockDetails?.stockLevel === 0 ? (
                                <span className="text-[#FF3D1D]">Stockout</span>
                            ) : (
                                <span className="text-[#56B648]">Sufficient</span>
                            )}
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b",
            },
            {
                content: (
                    <div className={`py-3 pr-3 font-rocGroteskMedium text-sm text-slate-800 `}>
                        <div>
                            <span className="text-sm font-rocGroteskMedium">
                                {!product?.forcastData?.incidentType ? (
                                    "Not Available"
                                ) : product?.forcastData?.incidentType?.toLowerCase() !==
                                  "stockout" ? (
                                    <span className=""> Overstocked </span>
                                ) : differenceInDays > 0 && differenceInDays <= 7 ? (
                                    <>
                                        <span className="text-[#E4281D] block"> Immediately </span>
                                        <span className={` text-slate-500 block`}>
                                            {moment().format("MMM DD, YYYY")}
                                        </span>
                                    </>
                                ) : differenceInDays < 0 ? (
                                    "Not Available"
                                ) : (
                                    <>
                                        <span
                                            className={`block ${
                                                differenceInDays > 7
                                                    ? "text-[#56B648]"
                                                    : "text-[#FF3D1D]"
                                            }`}
                                        >
                                            {differenceInDays === 0
                                                ? "Today"
                                                : formatMoney().format(differenceInDays) + " days"}
                                        </span>
                                        <span className={`block text-slate-500`}>
                                            {moment(product?.forcastData?.stockoutDate).format(
                                                "MMM DD, YYYY"
                                            )}
                                        </span>
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                ),
                widthClass: "!border-slate-200 !border-b",
            },
        ];
    });

    return (
        <div>
            {resultMessage?.products?.length > 0 && resultMessage?.success && (
                <>
                    <div className="overflow-x-scroll mb-6">
                        <CustomTable
                            tableBody={tableBody}
                            tableHeader={tableHeader}
                            isCellBordered={true}
                            isCellSelectable={false}
                            isAllSelectable={false}
                            stickyHeader={true}
                            isScrollable={true}
                            headerContainerClass="!bg-slate-50 !border-slate-200"
                            headerItemClass="font-rocGroteskMedium "
                            tableClass="mb-12 !border-slate-200"
                            allCellClick={true}
                            scrollContentId="table-container-one"
                            renderEmptyTable={() => null}
                        />
                    </div>
                    <div className="flex justify-end mt-5">
                        <ResponsivePaginationComponent
                            current={page?.currentPage}
                            total={resultMessage?.pagination?.number_of_pages}
                            maxWidth={350}
                            onPageChange={(page) => {
                                setPage((prev) => ({
                                    ...prev,
                                    currentPage: page,
                                }));
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default ViewProductList;
